/** PinPad — 6-digit numeric input with auto-advance + dotted display.
 *
 * Renders 6 boxes. Typing a digit fills it and moves focus to the next box.
 * Backspace clears the current box and moves back. Pasting a 6-digit string
 * fills all boxes. Emits `onComplete(pin)` when 6 digits are entered.
 *
 * Used in: Login (PIN mode) and the post-login "Set up PIN" modal.
 */
import { useEffect, useRef, useState } from "react";

export default function PinPad({ value = "", onChange, onComplete, disabled = false, autoFocus = true, "data-testid": testid = "pin-pad" }) {
  const [digits, setDigits] = useState(() => value.padEnd(6, " ").split("").slice(0, 6).map((c) => (c === " " ? "" : c)));
  const refs = useRef([]);

  // Keep internal state in sync if parent resets value
  useEffect(() => {
    if (value === digits.join("")) return;
    setDigits(value.padEnd(6, " ").split("").slice(0, 6).map((c) => (c === " " ? "" : c)));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);

  const emit = (next) => {
    onChange?.(next.join(""));
    if (next.every((d) => d !== "") && next.join("").length === 6) {
      onComplete?.(next.join(""));
    }
  };

  const handleChange = (i, raw) => {
    if (disabled) return;
    const v = raw.replace(/\D/g, "");
    if (!v) {
      const next = [...digits]; next[i] = ""; setDigits(next); emit(next);
      return;
    }
    if (v.length > 1) {
      // pasted multi-digit value
      const chars = v.slice(0, 6 - i).split("");
      const next = [...digits];
      chars.forEach((c, idx) => { next[i + idx] = c; });
      setDigits(next); emit(next);
      const focusIndex = Math.min(i + chars.length, 5);
      refs.current[focusIndex]?.focus();
      return;
    }
    const next = [...digits]; next[i] = v; setDigits(next); emit(next);
    if (i < 5) refs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < 5) {
      refs.current[i + 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid={testid}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="tel"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={i === 0 ? 6 : 1}  // first box accepts pasted 6-digit
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 border-stone-200 focus:border-[#f38838] focus:outline-none disabled:bg-stone-100 disabled:opacity-50"
          data-testid={`${testid}-${i}`}
        />
      ))}
    </div>
  );
}
